import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button.jsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.jsx";
import { mathUtils, cn } from "@/lib/utils.js";
import {
  segmentedTabIdle,
  segmentedTabSelected,
  segmentedTabTrack,
} from "@/lib/status-styles.js";

/**
 * Table Pagination Component
 * Shared pagination UI with rows per page dropdown and page navigation
 * @param {Object} props
 * @param {number} props.currentPage - Current page number
 * @param {number} props.totalPages - Total number of pages
 * @param {number} props.itemsPerPage - Number of rows per page
 * @param {number} props.totalItems - Total number of items
 * @param {number} props.startIndex - Start index for current page
 * @param {Function} props.onPageChange - Callback when page changes
 * @param {Function} props.onItemsPerPageChange - Callback when rows per page changes
 * @param {number[]} [props.itemsPerPageOptions] - Options for rows per page dropdown (default: [10, 25, 50, 100])
 * @param {string} [props.itemsLabel] - Label for items (default: "results")
 * @returns {JSX.Element} Table pagination component
 */
export default function TablePagination({
  currentPage,
  totalPages,
  itemsPerPage,
  totalItems,
  startIndex,
  onPageChange,
  onItemsPerPageChange,
  itemsPerPageOptions = [10, 25, 50, 100],
  itemsLabel = "results",
}) {
  const adjustedCurrentPage =
    currentPage > totalPages && totalPages > 0 ? 1 : currentPage;
  const endIndex = mathUtils.min(startIndex + itemsPerPage, totalItems);

  // Only show pagination if there are items
  if (totalItems === 0) return null;

  return (
    <div className='bg-card px-6 py-3 flex items-center justify-between border-t border-border rounded-b-lg'>
      <div className='text-sm text-muted-foreground font-light'>
        Showing <span className='font-medium text-foreground'>{startIndex + 1}</span> to{" "}
        <span className='font-medium text-foreground'>{endIndex}</span> of{" "}
        <span className='font-medium text-foreground'>{totalItems}</span> {itemsLabel}
      </div>
      <div className='flex items-center space-x-2'>
        <span className='text-sm text-muted-foreground font-light'>Rows per page:</span>
        <Select
          value={String(itemsPerPage)}
          onValueChange={(value) => onItemsPerPageChange(Number(value))}>
          <SelectTrigger className='h-8 w-[100px] text-sm border-border bg-background text-foreground hover:bg-muted shadow-none'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className='bg-popover border border-border shadow-md'>
            {itemsPerPageOptions.map((option) => (
              <SelectItem
                key={option}
                value={String(option)}
                className='text-sm text-foreground hover:bg-muted cursor-pointer'>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {totalPages > 1 && (
          <div
            className={segmentedTabTrack}
            role='navigation'
            aria-label='Pagination'>
            <Button
              variant='ghost'
              size='sm'
              className={cn(
                "h-7 w-7 p-0 shadow-none hover:bg-transparent",
                segmentedTabIdle,
                "disabled:opacity-40 disabled:cursor-not-allowed"
              )}
              onClick={() =>
                onPageChange(mathUtils.max(1, adjustedCurrentPage - 1))
              }
              disabled={adjustedCurrentPage === 1}
              aria-label='Previous page'>
              <ChevronLeft className='h-4 w-4' />
            </Button>

            {[...Array(mathUtils.min(3, totalPages))].map((_, i) => {
              const pageNum = adjustedCurrentPage - 1 + i;
              if (pageNum < 1 || pageNum > totalPages) return null;
              const isSelected = pageNum === adjustedCurrentPage;
              return (
                <Button
                  key={pageNum}
                  variant='ghost'
                  size='sm'
                  aria-current={isSelected ? "page" : undefined}
                  className={cn(
                    "h-7 min-w-7 px-2 shadow-none hover:bg-transparent font-medium",
                    isSelected ? segmentedTabSelected : segmentedTabIdle
                  )}
                  onClick={() => onPageChange(pageNum)}>
                  {pageNum}
                </Button>
              );
            })}

            <Button
              variant='ghost'
              size='sm'
              className={cn(
                "h-7 w-7 p-0 shadow-none hover:bg-transparent",
                segmentedTabIdle,
                "disabled:opacity-40 disabled:cursor-not-allowed"
              )}
              onClick={() =>
                onPageChange(mathUtils.min(totalPages, adjustedCurrentPage + 1))
              }
              disabled={adjustedCurrentPage === totalPages}
              aria-label='Next page'>
              <ChevronRight className='h-4 w-4' />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
